// istanbul ignore file - I'd have a hell of a hard time testing this

import { G2 } from "@ant-design/plots";
import * as _ from "lodash";

import { EventEntity, EventSourceType, ExtendedEventEntity, Line } from "@ctra/api";
import { Events } from "@ctra/components";

import {
  AnomalyPoint,
  makeLine,
  partition,
  simplify,
  findOrphans,
  makeOrphanLine,
  refill,
  makeOrphanCircle
} from "./utils";
const { eventCategoryMap } = Events;

// const factories = G2.getShapeFactory("point");
// const defaultPoint: G2.Types.Shape = _.get(factories, factories.defaultShapeType);
//
// /**
//  * Register a point with inheritance
//  */
// G2.registerShape(
//   "point",
//   "with-inheritance",
//   _.extend({}, defaultPoint, {
//     draw(cfg, container) {
//       defaultPoint.draw.apply(this, [cfg, container]);
//     }
//   } as G2.Types.Shape)
// );

/**
 * Draw anomaly lines
 * @param {Record<string, unknown>} attrs
 * @param {{showMissingData: boolean}} options
 * @return {(cfg, container) => IGroup}
 */
const makeDrawAnomalyLine: (
  attrs: Record<string, unknown>,
  options?: {
    orphan?: "circle" | "dash";
    showMissingData?: boolean;
    simplify?: boolean;
  }
) => G2.Types.RegisterShape["draw"] =
  (
    attrs,
    options = {
      showMissingData: false,
      simplify: false
    }
  ) =>
  (cfg, container) => {
    const group = container.addGroup();
    const points = _.get(cfg, ["points"], []);
    const lineData = _.get(cfg, ["data"], []) as Line;

    /**
     * Partition the data into missing and existing
     */
    const [missingData] = partition(points, lineData, (point) => !_.isNumber(_.get(point, ["y"], null)));

    /**
     * Partition the existing data into anomalies and ordinaries
     */
    const [anomalies, ordinaries] = partition(
      // todo use existingData instead of points
      points,
      lineData,
      (point) => {
        return (
          !_.isNumber(_.get(point, ["y"], null)) ||
          !_.isEmpty(_.get(point, ["anomaly"], null)) ||
          _.get(point, ["isolated"], null) === "both"
        );
      }
    );

    /**
     * Add the ordinary path to the chart
     */
    group.addShape("path", {
      attrs: {
        path: makeLine(ordinaries),
        ...cfg.defaultStyle,
        stroke: cfg.color
      },
      name: "ordinaries"
    });

    /**
     * Add the special points to the chart
     */
    group.addShape("path", {
      attrs: {
        path: makeLine(refill(options.simplify ? simplify(anomalies) : anomalies), (prev, current) => {
          return _.get(current, ["type"]) === AnomalyPoint.lead ? "M" : "L";
        }),
        ...cfg.defaultStyle,
        stroke: cfg.color,
        ...attrs
      },
      name: "anomalies"
    });

    /**
     * Make the orphan line
     */
    group.addShape("path", {
      attrs: {
        path:
          options.orphan === "circle"
            ? makeOrphanCircle(findOrphans(points, lineData))
            : makeOrphanLine(findOrphans(points, lineData)),
        ...cfg.defaultStyle,
        fill: options.orphan === "circle" ? cfg.color : void 0,
        stroke: cfg.color
      },
      name: "orphans"
    });

    if (options.showMissingData) {
      const simplified = simplify(missingData);
      const chunks = _.chunk(_.compact(simplified), 2);

      _.forEach(chunks, ([start, end]) => {
        if (
          ![AnomalyPoint.lead, AnomalyPoint.join].includes(start.type as AnomalyPoint) &&
          ![AnomalyPoint.trail, AnomalyPoint.join].includes(start.type as AnomalyPoint)
        ) {
          console.error("Your start and end points are not lead and trail points.", start, end);
        } else {
          const { x: x1 } = start as G2.Types.Point;
          const { x: x2 } = end as G2.Types.Point;
          const margin = 2;

          group.addShape("rect", {
            attrs: {
              width: x2 - (x1 + margin) - margin,
              height: 1000, // a big enough number to cover the whole chart
              x: x1 + margin,
              y: 0,
              fill: "rgba(0, 0 ,0 ,0.15)",
              lineWidth: 0
            },
            name: "rect-shape"
          });
        }
      });
    }

    return group;
  };

G2.registerShape("line", "hidden-anomaly-line", {
  draw: makeDrawAnomalyLine(
    {
      lineDash: [4, 4]
    },
    {
      showMissingData: false,
      simplify: true
    }
  )
});

G2.registerShape("line", "isolated-line", {
  draw: makeDrawAnomalyLine(
    {
      stroke: 0
    },
    {
      orphan: "circle",
      showMissingData: false,
      simplify: false
    }
  )
});

/**
 * Rewrite the line constructor to enable split-lines for missing data and anomalies
 */
G2.registerShape("line", "anomaly-line", {
  draw: makeDrawAnomalyLine(
    {
      stroke: "#CF1322"
    },
    {
      orphan: "dash",
      showMissingData: true,
      simplify: false
    }
  )
});

/**
 * Register a point for anomalies
 */
G2.registerShape("point", "anomaly-point", {
  draw(cfg, container) {
    const { x, y, data } = cfg;
    const anomaly = _.get(data, ["anomaly"], null);

    if (anomaly) {
      const { type } = anomaly;
      const group = container.addGroup();
      const isExplained = _.startsWith(type, "explained");

      group.addShape("circle", {
        attrs: {
          x: x as number,
          y: y as number,
          r: 4,
          fill: isExplained ? "#389e0d" : "#CF1322",
          opacity: 1
        }
      });

      return group;
    }
  }
});

G2.registerShape("point", "sparse-data-point", {
  draw(cfg, container) {
    const { x, y, data, color } = cfg;
    const isolated = _.get(data, ["isolated"], false);

    if (!isolated) {
      const group = container.addGroup();

      group.addShape("circle", {
        attrs: {
          x: x as number,
          y: y as number,
          r: 4,
          fill: color,
          opacity: 1
        }
      });

      return group;
    }
  }
});

/**
 * Make an interactive farm-event point
 */
G2.registerShape("point", "farm-event-point", {
  draw({ x, y, data, ...rest }, container) {
    const radius = 16;
    const top = 32;
    const events = _.get(data, ["events"], []);

    if (events.length) {
      const { category } = _.head(events) as ExtendedEventEntity;
      const [, , icon] = eventCategoryMap[category];

      const hasAiModel = _.some(
        events,
        (event: EventEntity) => event.source.type === EventSourceType.aiModel
      );

      const group: G2.IGroup = container.addGroup();

      group.addShape("rect", {
        name: "click-area",
        attrs: {
          x: Number(x) - radius,
          y: 0,
          width: radius * 2,
          height: Number(y),
          fill: "#ccc",
          opacity: 0
        }
      });

      group.addShape("path", {
        name: "line",
        attrs: {
          filter: hasAiModel ? "hue-rotate(180deg)" : void 0,
          path: [
            ["M", x, 0],
            ["L", x, Number(y)]
          ],
          stroke: "#d9d9d9",
          lineWidth: 1,
          lineDash: [4, 4]
        }
      });

      const img = group.addShape("image", {
        name: "icon",
        attrs: {
          filter: hasAiModel ? "hue-rotate(180deg)" : void 0,
          x: Number(x) - radius / 2,
          y: top - radius / 2,
          img: icon,
          width: radius,
          height: radius
        }
      });

      group.addShape("circle", {
        name: "circle",
        attrs: {
          filter: hasAiModel ? "hue-rotate(180deg)" : void 0,
          x: Number(x),
          y: top,
          r: radius,
          stroke: "#007180",
          fill: "#ffffff"
        }
      });

      img.toFront();

      return group;
    }
  }
});
